import { Box, Typography } from '@mui/material';
import React from 'react'
import { SmallBold, SmallRegular, LargeBold, Point } from './GettingStarted';

export const Skill = ({ bg, name, disableMargin = false, initial = name.slice(0, 1) }: { bg: string; name: string; disableMargin?: boolean; initial?: string}) => {
    return (
        <Box display={{ xs: 'block', md: 'inline'}}>
            <Box
                ml={disableMargin ? 0 : "25px"}
                p="6px 16px"
                borderRadius="4px"
                bgcolor={bg}
                display="inline-flex"
                alignItems="center"
                gap="12px"
            >
                <Typography color="#fff" fontSize="14px" fontWeight={600}>
                    {name}
                </Typography>
                <Typography
                    fontSize="14px"
                    color="#fff"
                    fontWeight={700}
                    sx={{
                        p: '0 5px',
                        bgcolor: 'rgba(0, 0, 0, .4)',
                        borderRadius: '4px'
                    }}
                >
                    {initial}
                </Typography>
            </Box>
        </Box>
    );
};

const UnderstandingSkill = () => {
  return (
      <Box>
          <Box mb="45px">
              <Box mb="25px">
                  <LargeBold>
                      Understanding Skill Competency Levels: A User Guide
                  </LargeBold>
              </Box>
              <Box>
                  <SmallRegular>
                      At Jobfactor, we believe in empowering our users to make
                      informed decisions when rating and reviewing the skills
                      and accomplishments of others. To ensure fairness and
                      accuracy, we've established a clear skill competency
                      rating scale consisting of five categories: beginner,
                      experienced, advanced, expert, and thought leader. This
                      guide will help you understand what each of these
                      categories means, allowing you to provide thoughtful and
                      unbiased ratings and reviews.
                  </SmallRegular>
              </Box>
          </Box>
          <Box mb="32px">
              <Box mb="25px">
                  <SmallBold>
                      1. Beginner: <Skill name="Beginner" bg="#E75541" />
                  </SmallBold>
              </Box>
              <Box>
                  <Point>
                      Definition: A beginner is someone who is just starting to
                      acquire and develop the skill in question.
                  </Point>
                  <Point>
                      Characteristics: Individuals in this category typically
                      have limited practical experience and may still be in the
                      early stages of learning.
                  </Point>
                  <Point>
                      Example: A beginner programmer may have basic knowledge
                      but lacks extensive hands-on experience.
                  </Point>
              </Box>
          </Box>
          <Box mb="32px">
              <Box mb="25px">
                  <SmallBold>
                      2. Experienced: <Skill name="Experienced" bg="#F6C70E" />
                  </SmallBold>
              </Box>
              <Box>
                  <Point>
                      Definition: An experienced individual possesses a solid
                      foundation in the skill and has applied it in practical
                      situations.
                  </Point>
                  <br />
                  <Point>
                      Characteristics: Users in this category have demonstrated
                      their proficiency through hands-on experience and can
                      perform tasks confidently.
                  </Point>
                  <br />
                  <Point>
                      Example: An experienced marketer has successfully executed
                      multiple campaigns and can navigate various marketing
                      tools.
                  </Point>
              </Box>
          </Box>
          <Box mb="32px">
              <Box mb="25px">
                  <SmallBold>
                      3. Advanced: <Skill name="Advanced" bg="#49B6FF" />
                  </SmallBold>
              </Box>
              <Box>
                  <Point>
                      Definition: Advanced users have reached a higher level of
                      skill mastery and can handle complex tasks with
                      competence.
                  </Point>
                  <Point>
                      Characteristics: Individuals in this category are capable
                      of tackling challenging projects, troubleshooting
                      effectively, and often seek innovative solutions.
                  </Point>
                  <Point>
                      Example: An advanced graphic designer can create intricate
                      designs and is well-versed in design software.
                  </Point>
              </Box>
          </Box>
          <Box mb="32px">
              <Box mb="25px">
                  <SmallBold>
                      4. Expert: <Skill name="Expert" bg="#95C97A" initial='X' />
                  </SmallBold>
              </Box>
              <Box>
                  <Point>
                      Definition: An expert is someone who has achieved an
                      exceptional level of skill mastery and is recognized as a
                      subject matter authority.
                  </Point>
                  <Point>
                      Characteristics: Users in this category have extensive
                      experience, often contributing to the field through
                      thought leadership, publications, or mentorship.
                  </Point>
                  <Point>
                      Example: An expert data scientist is a pioneer in their
                      field, known for groundbreaking research and innovations.
                  </Point>
              </Box>
          </Box>
          <Box mb="32px">
              <Box mb="25px">
                  <SmallBold>
                      5. Thought Leader:{' '}
                      <Skill name="Thought Leader" bg="#07AF22" initial='L' />
                  </SmallBold>
              </Box>
              <Box>
                  <Point>
                      Definition: Thought leaders are at the forefront of their
                      industry, shaping trends, and influencing others.
                  </Point>
                  <Point>
                      Characteristics: Individuals in this category are
                      visionaries, contributing original ideas and inspiring
                      others through their expertise.
                  </Point>
                  <Point>
                      Example: A thought leader in sustainable energy has
                      influenced policies and practices, leading the way toward
                      a greener future.
                  </Point>
              </Box>
          </Box>
          <Box>
              <SmallRegular>
                  When rating someone's skills and accomplishments on Jobfactor,
                  we encourage you to consider these competency levels
                  carefully. Take into account the person's experience,
                  expertise, and contributions to the field when assigning a
                  rating. By doing so, you'll help create a fair and informative
                  community that benefits all users. <br /> Remember,
                  constructive and specific feedback in your reviews can provide
                  valuable insights to others in our community. Let's work
                  together to foster a platform where skills and accomplishments
                  are recognized accurately and fairly.
              </SmallRegular>
          </Box>
      </Box>
  );
}

export default UnderstandingSkill
